import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Search, Zap } from 'lucide-react'
import {
  adminPublicApiClient,
  companyPublicApiClient,
  type PlatformInvocationDetail,
  type PlatformInvocationRow,
} from '@/lib/api/admin-public-api.client'
import { Input } from '@/components/ui/input'
import {
  DATE_RANGES,
  type DateRangeValue,
  DetailField,
  FILTER_ALL,
  type FilterValue,
  PLATFORM_PAGE_SIZE,
  SectionTabs,
  sinceForRange,
  UNDERLINE_TABS_LIST,
  UNDERLINE_TABS_TRIGGER,
  formatTimestamp,
} from './platform-shared'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingPagination } from '@/components/ui/FloatingPagination'

type StatusFilter = FilterValue<PlatformInvocationRow['status']>

const STATUS_FILTERS: readonly StatusFilter[] = [
  FILTER_ALL,
  'ACCEPTED',
  'REJECTED',
  'FAILED',
]

const STATUS_BADGE_CLASSES: Record<PlatformInvocationRow['status'], string> = {
  ACCEPTED:
    'border-transparent bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs',
  REJECTED:
    'border-transparent bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs',
  FAILED: 'border-transparent bg-red-100 text-red-700 hover:bg-red-100 text-xs',
}

const STATUS_LABELS: Record<PlatformInvocationRow['status'], string> = {
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  FAILED: 'Failed',
}

export function PlatformInvocationsPage() {
  const { isInitialized, currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id
  const [rows, setRows] = useState<PlatformInvocationRow[] | null>(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(FILTER_ALL)
  const [dateRange, setDateRange] = useState<DateRangeValue>(FILTER_ALL)
  const [searchInput, setSearchInput] = useState('')
  const [searchFilter, setSearchFilter] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const triggerFilter = searchParams.get('triggerId')
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState<PlatformInvocationDetail | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  // Guards against a slow response for a previous filter landing after a
  // newer one and overwriting it
  const requestSeq = useRef(0)

  useEffect(() => {
    const handle = setTimeout(() => setSearchFilter(searchInput.trim()), 400)
    return () => clearTimeout(handle)
  }, [searchInput])

  const loadPage = useCallback(
    async (targetPage: number) => {
      if (!companyId) return
      const requestId = ++requestSeq.current
      try {
        setLoading(true)
        const params = {
          status: statusFilter === FILTER_ALL ? undefined : statusFilter,
          triggerId: triggerFilter ?? undefined,
          search: searchFilter || undefined,
          since: sinceForRange(dateRange),
          limit: PLATFORM_PAGE_SIZE,
          page: String(targetPage),
        }
        const result = isSuperAdmin
          ? await adminPublicApiClient.listInvocations({
              ...params,
              companyId,
            })
          : await companyPublicApiClient.listInvocations(params)
        if (requestId !== requestSeq.current) return
        setRows(result.items)
        setTotal(result.total)
        setPage(result.page)
      } catch (error) {
        if (requestId !== requestSeq.current) return
        toast.error(
          error instanceof Error ? error.message : 'Failed to load invocations',
        )
        setRows([])
      } finally {
        if (requestId === requestSeq.current) setLoading(false)
      }
    },
    [
      companyId,
      isSuperAdmin,
      statusFilter,
      searchFilter,
      dateRange,
      triggerFilter,
    ],
  )

  useEffect(() => {
    if (!isInitialized) return
    loadPage(1)
  }, [isInitialized, loadPage])

  const openDetail = async (row: PlatformInvocationRow) => {
    setDetailOpen(true)
    setDetail(null)
    try {
      setDetail(
        isSuperAdmin
          ? await adminPublicApiClient.getInvocation(row.id)
          : await companyPublicApiClient.getInvocation(row.id),
      )
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to load invocation',
      )
      setDetailOpen(false)
    }
  }

  if (!isInitialized) return null

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0">
          <div className="mt-6">
            <PageHeader title="Triggers" />
          </div>
          <SectionTabs
            tabs={[
              {
                label: 'Configuration',
                to: ROUTE_REGISTRY.TRIGGERS.path,
                active: false,
              },
              {
                label: 'Events',
                to: ROUTE_REGISTRY.TRIGGERS_EVENTS.path,
                active: true,
              },
            ]}
            actions={
              <>
                <Select
                  value={dateRange}
                  onValueChange={value => setDateRange(value as DateRangeValue)}
                >
                  <SelectTrigger className="h-9 w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={changeEvent =>
                      setSearchInput(changeEvent.target.value)
                    }
                    placeholder="Search event, error, or id…"
                    className="h-9 w-[240px] pl-8"
                  />
                </div>
                <Select
                  value={statusFilter}
                  onValueChange={value =>
                    setStatusFilter(value as StatusFilter)
                  }
                >
                  <SelectTrigger className="h-9 w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_FILTERS.map(status => (
                      <SelectItem key={status} value={status}>
                        {status === FILTER_ALL ? 'All statuses' : status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
          />
          {triggerFilter ? (
            <Badge variant="outline" className="w-fit gap-1 font-mono text-xs">
              trigger: {triggerFilter}
              <button
                type="button"
                aria-label="Clear trigger filter"
                onClick={() => setSearchParams({})}
                className="ml-1 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </Badge>
          ) : (
            <div />
          )}

          <Card className="w-full">
            <CardContent className="p-0">
              {rows === null ? (
                <div className="divide-y">
                  {[...Array(6)].map((_, index) => (
                    <div key={index} className="flex space-x-4 px-6 py-4">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  ))}
                </div>
              ) : rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Zap className="h-8 w-8 text-gray-400 mb-4" />
                  <h3 className="mb-2 text-lg font-semibold">
                    No invocations yet
                  </h3>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Every POST /v1/events lands here — one row per entry, with
                    its dispatch status and resulting call.
                  </p>
                </div>
              ) : (
                <div
                  className={cn(
                    'transition-opacity duration-200',
                    loading && 'pointer-events-none opacity-50',
                  )}
                >
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Received</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow
                          key={row.id}
                          className="cursor-pointer"
                          onClick={() => openDetail(row)}
                        >
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {formatTimestamp(row.createdAt)}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {row.event}
                            {row.entryIndex > 0 && (
                              <span className="text-muted-foreground">
                                {' '}
                                #{row.entryIndex}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_BADGE_CLASSES[row.status]}>
                              {STATUS_LABELS[row.status]}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className="max-w-[260px] truncate text-sm text-muted-foreground"
                            title={
                              row.errorMessage ?? row.errorCode ?? undefined
                            }
                          >
                            {row.errorMessage ?? row.errorCode ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <FloatingPagination
                    componentId="invocations-pagination"
                    itemType="events"
                    total={total}
                    page={page}
                    pageSize={Number(PLATFORM_PAGE_SIZE)}
                    loading={loading}
                    onPage={loadPage}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
          <SheetContent className="w-full overflow-y-auto p-6 sm:max-w-[560px]">
            {detail === null ? (
              <div className="space-y-3 pt-8">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2 font-mono text-sm">
                    {detail.id}
                    <Badge className={STATUS_BADGE_CLASSES[detail.status]}>
                      {STATUS_LABELS[detail.status]}
                    </Badge>
                  </SheetTitle>
                  <SheetDescription>
                    {detail.event} · {formatTimestamp(detail.createdAt)}
                  </SheetDescription>
                </SheetHeader>

                {detail.errorCode && (
                  <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-xs dark:border-red-900 dark:bg-red-950">
                    <div className="font-mono font-medium text-red-800 dark:text-red-300">
                      {detail.errorCode}
                    </div>
                    {detail.errorMessage && (
                      <div className="mt-1 break-all text-red-700 dark:text-red-400">
                        {detail.errorMessage}
                      </div>
                    )}
                  </div>
                )}

                <Tabs defaultValue="outcome" className="mt-4">
                  <TabsList className={UNDERLINE_TABS_LIST}>
                    <TabsTrigger
                      value="outcome"
                      className={UNDERLINE_TABS_TRIGGER}
                    >
                      Outcome
                    </TabsTrigger>
                    <TabsTrigger
                      value="metadata"
                      className={UNDERLINE_TABS_TRIGGER}
                    >
                      Metadata
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="outcome" className="mt-3 space-y-3">
                    <div className="divide-y">
                      <DetailField label="Status" chip={false}>
                        <Badge className={STATUS_BADGE_CLASSES[detail.status]}>
                          {STATUS_LABELS[detail.status]}
                        </Badge>
                      </DetailField>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Invocations record acceptance only — request payloads are
                      not persisted, and execution outcomes are delivered via
                      webhooks.
                    </p>
                    {detail.batchEntries.length > 1 && (
                      <div>
                        <h4 className="mb-2 text-sm font-medium">
                          Batch entry outcomes ({detail.batchEntries.length}{' '}
                          entries — this row is entry #{detail.entryIndex})
                        </h4>
                        <div className="divide-y rounded-md border">
                          {detail.batchEntries.map(entry => (
                            <div
                              key={entry.id}
                              className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                            >
                              <span className="shrink-0 font-mono text-muted-foreground">
                                #{entry.entryIndex}
                              </span>
                              <span className="min-w-0 flex-1 truncate text-muted-foreground">
                                {entry.errorMessage ?? '—'}
                              </span>
                              <Badge
                                className={STATUS_BADGE_CLASSES[entry.status]}
                              >
                                {STATUS_LABELS[entry.status]}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="metadata" className="mt-3">
                    <div className="divide-y">
                      <DetailField label="Status" chip={false}>
                        <Badge className={STATUS_BADGE_CLASSES[detail.status]}>
                          {STATUS_LABELS[detail.status]}
                        </Badge>
                      </DetailField>
                      <DetailField label="Invocation">{detail.id}</DetailField>
                      <DetailField label="Request ID">
                        {detail.requestId}
                      </DetailField>
                      <DetailField label="Trigger">
                        {detail.triggerId ?? '—'}
                      </DetailField>
                      <DetailField label="API key">
                        {detail.apiKey
                          ? `${detail.apiKey.name ?? 'Unnamed'}${detail.apiKey.keyLastFour ? ` · sk_super_…${detail.apiKey.keyLastFour}` : ''}`
                          : detail.apiKeyId}
                      </DetailField>
                      <DetailField label="Received">
                        {formatTimestamp(detail.createdAt)}
                      </DetailField>
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </SheetContent>
        </Sheet>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
